import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { Eye, EyeOff } from 'lucide-react';
import { EmailVerification } from '../../components/auth/EmailVerification';
import styles from './RegisterPage.module.scss';

interface RegisterForm {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  terms: boolean;
}

export const RegisterPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const { register: registerUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get pricing plan info from navigation state
  const selectedPlan = location.state?.selectedPlan;
  const planPrice = location.state?.price;
  const billingPeriod = location.state?.billingPeriod;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterForm>();

  const onSubmit = async (data: RegisterForm) => {
    try {
      // Step 1: Register user (this will send verification email)
      await registerUser({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
      });

      // Show verification step
      setUserEmail(data.email);
      setShowVerification(true);
      toast.success('Registration successful! Please check your email for a verification code.');
    } catch (error: any) {
      toast.error(error.message || 'Registration failed');
    }
  };

  const onVerifyEmail = async () => {
    if (!verificationCode.trim()) {
      toast.error('Please enter the verification code');
      return;
    }

    setIsVerifying(true);
    try {
      // Call your verification endpoint
      const response = await fetch('/api/auth/verify-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: userEmail,
          code: verificationCode,
        }),
      });

      if (!response.ok) {
        throw new Error('Verification failed');
      }

      if (selectedPlan) {
        toast.success(`Email verified! Setting up ${selectedPlan} plan...`);
        navigate('/dashboard', {
          state: {
            message: `Welcome! Your ${selectedPlan} plan is being set up.`,
            selectedPlan,
            planPrice,
            billingPeriod,
          },
        });
      } else {
        toast.success('Email verified! Welcome to Odyssey!');
        navigate('/dashboard');
      }
    } catch (error: any) {
      toast.error(error.message || 'Verification failed');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleVerificationSuccess = () => {
    if (selectedPlan) {
      toast.success(`Email verified! Setting up ${selectedPlan} plan...`);
      navigate('/dashboard', {
        state: {
          message: `Welcome! Your ${selectedPlan} plan is being set up.`,
          selectedPlan,
          planPrice,
          billingPeriod,
        },
      });
    } else {
      toast.success('Email verified! Welcome to Odyssey!');
      navigate('/dashboard');
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.formCard}>
          {/* Logo */}
          <div className={styles.logo}>
            <div className={styles.logoIcon}>
              <div className={styles.iconLayers}>
                <div className={styles.layerBack}></div>
                <div className={styles.layerMiddle}></div>
                <div className={styles.layerFront}></div>
              </div>
              <div className={styles.iconSpark}>✦</div>
            </div>
          </div>

          {/* Dynamic Title Based on Step */}
          {showVerification ? (
            <>
              <h1 className={styles.title}>Verify your email</h1>
              <p className={styles.subtitle}>
                We've sent a 6-digit code to <strong>{userEmail}</strong>
              </p>
            </>
          ) : (
            <>
              <h1 className={styles.title}>Create your account</h1>
              {selectedPlan ? (
                <div className={styles.planInfo}>
                  <p className={styles.selectedPlan}>
                    Selected: <strong>{selectedPlan}</strong> plan
                  </p>
                  <p className={styles.planPrice}>
                    ${planPrice}/
                    {billingPeriod === 'yearly' ? 'month (billed yearly)' : 'month'}
                  </p>
                </div>
              ) : (
                <p className={styles.subtitle}>
                  Join thousands of learners advancing their careers.
                </p>
              )}
            </>
          )}

          {/* Google Button - only show if not in verification step */}
          {!showVerification && (
            <>
              <button type="button" className={styles.googleButton}>
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </button>

              {/* Divider */}
              <div className={styles.divider}>
                <span>or continue with</span>
              </div>
            </>
          )}

          {/* Form - only show if not in verification step */}
          {!showVerification && (
            <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
            <div className={styles.nameRow}>
              <div className={styles.nameField}>
                <input
                  type="text"
                  placeholder="First name"
                  className={styles.input}
                  autoFocus
                  {...register('firstName', {
                    required: 'First name is required',
                    minLength: {
                      value: 1,
                      message: 'First name is required',
                    },
                  })}
                />
                {errors.firstName && (
                  <span className={styles.error}>
                    {errors.firstName.message}
                  </span>
                )}
              </div>
              <div className={styles.nameField}>
                <input
                  type="text"
                  placeholder="Last name"
                  className={styles.input}
                  {...register('lastName', {
                    required: 'Last name is required',
                    minLength: {
                      value: 1,
                      message: 'Last name is required',
                    },
                  })}
                />
                {errors.lastName && (
                  <span className={styles.error}>
                    {errors.lastName.message}
                  </span>
                )}
              </div>
            </div>

            <input
              type="email"
              placeholder="Enter your email"
              className={styles.input}
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: 'Please enter a valid email',
                },
              })}
            />
            {errors.email && (
              <span className={styles.error}>{errors.email.message}</span>
            )}

            <div className={styles.passwordContainer}>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Create your password"
                className={styles.input}
                {...register('password', {
                  required: 'Password is required',
                  minLength: {
                    value: 6,
                    message: 'Password must be at least 6 characters',
                  },
                })}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={styles.passwordToggle}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.password && (
              <span className={styles.error}>{errors.password.message}</span>
            )}

            <label className={styles.termsLabel}>
              <input
                type="checkbox"
                className={styles.checkbox}
                {...register('terms', {
                  required: 'You must agree to the terms and conditions',
                })}
              />
              <span className={styles.termsText}>
                I agree to the{' '}
                <Link to="/terms" className={styles.termsLink}>
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className={styles.termsLink}>
                  Privacy Policy
                </Link>
              </span>
            </label>
            {errors.terms && (
              <span className={styles.error}>{errors.terms.message}</span>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className={styles.submitButton}
            >
              {isSubmitting ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>
          )}

          {/* Conditional Content: Show verification or sign-in link */}
          {showVerification ? (
            <EmailVerification
              email={userEmail}
              onSuccess={handleVerificationSuccess}
              selectedPlan={selectedPlan}
              planPrice={planPrice}
              billingPeriod={billingPeriod}
            />
          ) : (
            <>
              {/* Sign in link */}
              <p className={styles.signinText}>
                Already have an account?{' '}
                <Link to="/login" className={styles.signinLink}>
                  Sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
